import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import React from "react";
import { classNames } from "../../helpers";
import { useToast } from "../../provider/ToastProvider";
import axiosInstance from "../../utils/axiosInstance";
import { getUrlForBookings } from "../../utils/confighelpers";
import { FcModal, FieldError, FieldLabel, Icon, inputClass } from "../../modals/classes/schedule/ui";

/**
 * Receipts for club admins (2026-10-27): open a payment's receipt, or email it to the member again (like Stripe's
 * "Send receipt"). Used by Revenue › Recent transactions and the member page › Transactions.
 *
 * POST payments/paymentReceipt
 *   ref = { transactionId } (Revenue list row) | { paymentIntentId } | { invoiceId } (member page, Stripe ids)
 *   { ...ref, action: "details" } -> { receiptUrl, invoicePdfUrl, invoiceUrl, toEmail, memberName, itemName, amount }
 *   { ...ref, action: "email", toEmail } -> { sentTo }
 * The receipt link is fetched when clicked (Stripe receipt links stop working after 30 days).
 */

export const RECEIPT_ICON = (
  <>
    <path d="M6 2h12v20l-3-2-3 2-3-2-3 2z" />
    <path d="M9 7h6M9 11h6" />
  </>
);
export const MAIL_ICON = (
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </>
);

const DOTS = (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <circle cx="12" cy="5" r="1.6" fill="currentColor" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" />
    <circle cx="12" cy="19" r="1.6" fill="currentColor" />
  </svg>
);

/** "⋯" menu for a payment row: View receipt / Email receipt. */
export function ReceiptMenu({ label = "Receipt options", onView, onEmail }) {
  const items = [
    { label: "View receipt", icon: RECEIPT_ICON, onClick: onView },
    { label: "Email receipt to member", icon: MAIL_ICON, onClick: onEmail },
  ];
  return (
    <Menu as="div" className="relative flex justify-end">
      <MenuButton aria-label={label} className="flex h-9 w-9 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-900 data-[open]:bg-gray-100">
        {DOTS}
      </MenuButton>
      <MenuItems anchor="bottom end" className="z-[60] mt-1 w-60 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl focus:outline-none">
        {items.map((it) => (
          <MenuItem key={it.label}>
            <button type="button" onClick={it.onClick} className={classNames("flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-gray-900 data-[focus]:bg-[#F7F4F8]")}>
              <Icon className="h-4 w-4 text-gray-500" d={it.icon} />
              {it.label}
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const euro = (n) => `€${(Math.round(Number(n || 0) * 100) / 100).toFixed(2).replace(".", ",")}`;

const callReceipt = async (data) => {
  const res = await axiosInstance({ method: "post", url: `${getUrlForBookings()}payments/paymentReceipt`, data });
  const body = res?.data || {};
  if (res?.status === 200 && body.statusCode === 200) return { ok: true, data: body.data || {} };
  const msg = typeof body.message === "string" ? body.message : body.message?.message;
  return { ok: false, message: msg || "Something went wrong. Please try again." };
};

/**
 * const receipts = useReceiptActions();
 *   receipts.view(ref)   opens the receipt in a new tab
 *   receipts.email(ref)  opens the "Email receipt" pop-up
 *   {receipts.modal}     render once
 */
export function useReceiptActions() {
  const { addErrorToast, addSuccessToast } = useToast();
  const [emailRef, setEmailRef] = React.useState(null);

  const view = React.useCallback(
    async (ref) => {
      // Open the tab now (inside the click), point it at the receipt when we have the link: no pop-up blocker.
      const tab = window.open("", "_blank");
      if (tab) {
        try {
          tab.opener = null;
          tab.document.title = "Opening receipt…";
          tab.document.body.innerHTML = '<p style="font-family:Helvetica,Arial,sans-serif;color:#555;padding:24px">Opening receipt…</p>';
        } catch (e) {
          /* ignore */
        }
      }
      try {
        const r = await callReceipt({ ...ref, action: "details" });
        const url = r.ok ? r.data.receiptUrl || r.data.invoiceUrl || r.data.invoicePdfUrl : "";
        if (url) {
          if (tab) tab.location.href = url;
          else window.open(url, "_blank", "noopener");
          return;
        }
        if (tab) tab.close();
        addErrorToast(r.ok ? "There's no receipt for this payment yet." : r.message);
      } catch (e) {
        if (tab) tab.close();
        addErrorToast("Couldn't open the receipt. Please try again.");
      }
    },
    [addErrorToast]
  );

  const modal = <EmailReceiptModal reference={emailRef} onClose={() => setEmailRef(null)} onSent={(to) => addSuccessToast(`Receipt sent to ${to}`)} />;
  return { view, email: setEmailRef, modal };
}

function EmailReceiptModal({ reference, onClose, onSent }) {
  const open = !!reference;
  const [info, setInfo] = React.useState(null); // details from the server
  const [loading, setLoading] = React.useState(false);
  const [to, setTo] = React.useState("");
  const [error, setError] = React.useState("");
  const [sending, setSending] = React.useState(false);

  React.useEffect(() => {
    if (!reference) return undefined;
    let alive = true;
    setInfo(null);
    setError("");
    setTo("");
    setLoading(true);
    callReceipt({ ...reference, action: "details" })
      .then((r) => {
        if (!alive) return;
        if (r.ok) {
          setInfo(r.data);
          setTo(r.data.toEmail || "");
        } else setError(r.message);
      })
      .catch(() => alive && setError("Couldn't load this payment. Please try again."))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [reference]);

  const email = to.trim();
  const valid = EMAIL_RE.test(email);

  const send = async () => {
    if (!valid) {
      setError("Please enter a valid email address.");
      return;
    }
    setSending(true);
    setError("");
    try {
      const r = await callReceipt({ ...reference, action: "email", toEmail: email });
      if (r.ok) {
        onSent(r.data.sentTo || email);
        onClose();
      } else setError(r.message);
    } catch (e) {
      setError("Couldn't send the receipt. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const first = String(info?.memberName || "").trim().split(/\s+/)[0];
  return (
    <FcModal
      open={open}
      onClose={onClose}
      eyebrow="Receipt"
      title="Email receipt"
      subtitle={info ? `${info.itemName} · ${euro(info.amount)}${info.memberName ? ` · ${info.memberName}` : ""}` : " "}
      primaryLabel="Send receipt"
      onPrimary={send}
      busy={sending}
      primaryDisabled={loading || !info || !email}
      footerNote="Sent in your club's name. Replies come to your club email."
    >
      {loading ? (
        <div className="flex flex-col gap-3" aria-hidden="true">
          <div className="h-4 w-2/3 animate-pulse rounded-lg bg-[#F3EEF5]" />
          <div className="h-12 animate-pulse rounded-xl bg-[#F3EEF5]" />
        </div>
      ) : info ? (
        <div>
          <p className="mb-4 text-sm leading-6 text-gray-700">
            {first ? `${first} gets` : "The member gets"} the receipt by email: what they bought, the amount, payment method and a link to the payment receipt
            {info.invoicePdfUrl ? " and invoice PDF" : ""}.
          </p>
          <FieldLabel htmlFor="receipt-to">Send to</FieldLabel>
          <input
            id="receipt-to"
            type="email"
            value={to}
            maxLength={120}
            autoComplete="off"
            placeholder="member@email.com"
            onChange={(e) => {
              setTo(e.target.value);
              if (error) setError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && send()}
            className={inputClass(!!error)}
          />
          {!info.toEmail && !error && <p className="mt-1.5 text-xs text-gray-500">We don't have an email address for this member. Type the one they'd like to use.</p>}
          <FieldError>{error}</FieldError>
        </div>
      ) : (
        <p className="rounded-2xl bg-[#FFF5F6] px-4 py-3 text-sm font-bold text-[#B4232F]">{error || "Couldn't load this payment."}</p>
      )}
    </FcModal>
  );
}
